import { errorMessageFrom } from "@moeru/std";
import { createClient as createClient$1 } from "@proj-airi/better-ws";
import { createCrossWsConnector } from "@proj-airi/better-ws/client/crossws";
import { isTerminalAuthenticationServerErrorMessage, parseServerErrorMessage } from "@proj-airi/server-shared";
import { ContextUpdateStrategy, MessageHeartbeat, MessageHeartbeatKind, WebSocketEventSource } from "@proj-airi/server-shared/types";
import { parse, stringify } from "superjson";
import { check, objectWithRest, pipe, safeParse, string, unknown } from "valibot";
//#region src/codec.ts
const invalidMessage = "Invalid AIRI websocket message.";
const eventDataSchema = pipe(unknown(), check((value) => Boolean(value) && typeof value === "object" && !Array.isArray(value), "Expected event data to be a non-array object."));
const eventEnvelopeSchema = objectWithRest({
	type: string(),
	data: eventDataSchema
}, unknown());
/** Error thrown when websocket text cannot be parsed as an AIRI event envelope. */
var InvalidMessageError = class extends Error {
	source;
	constructor(options = {}) {
		super(invalidMessage, { cause: options.cause });
		this.name = "InvalidMessageError";
		this.source = options.source;
	}
};
/** Parses one AIRI websocket protocol event from SuperJSON or plain JSON text. */
function parseEvent(text) {
	let superJsonParsed;
	let superJsonError;
	try {
		superJsonParsed = parse(text);
	} catch (error) {
		superJsonError = error;
	}
	const potentialEvent = superJsonParsed && typeof superJsonParsed === "object" && "type" in superJsonParsed ? superJsonParsed : parsePlainJson(text, superJsonError);
	const result = safeParse(eventEnvelopeSchema, potentialEvent);
	if (!result.success) throw new InvalidMessageError({
		cause: result.issues,
		source: potentialEvent
	});
	return potentialEvent;
}
/** Serializes one AIRI websocket protocol event with SuperJSON. */
function stringifyEvent(event) {
	return stringify(event);
}
function parsePlainJson(text, superJsonError) {
	try {
		return JSON.parse(text);
	} catch (jsonError) {
		throw new InvalidMessageError({
			cause: superJsonError ?? jsonError,
			source: text
		});
	}
}
//#endregion
//#region src/client.ts
function createInstanceId() {
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}
function createEventId() {
	return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}
function normalizeHeartbeatOptions(heartbeat) {
	if (heartbeat === false) return false;
	const readTimeout = heartbeat?.readTimeout ?? 3e4;
	const pingInterval = heartbeat?.pingInterval ?? Math.max(1e3, Math.floor(readTimeout / 2));
	return {
		readTimeout,
		pingInterval: Math.min(pingInterval, readTimeout),
		message: heartbeat?.message ?? MessageHeartbeat.Ping
	};
}
/** Wraps a text websocket connector with AIRI protocol serialization. */
function createTextProtocolConnector(textConnector) {
	return { async connect(events) {
		const connection = await textConnector.connect({
			message(text) {
				try {
					events.message(parseEvent(text));
				} catch (error) {
					events.error(error);
				}
			},
			close: (details) => events.close(details),
			error: (error) => events.error(error)
		});
		return {
			send: (message) => connection.send(stringifyEvent(message)),
			close: (code, reason) => connection.close?.(code, reason),
			ping: connection.ping,
			pong: connection.pong
		};
	} };
}
function createDefaultProtocolConnector(url) {
	return createTextProtocolConnector(createCrossWsConnector({ url }));
}
function normalizeOptions(options) {
	const url = options.url ?? "ws://localhost:6121/ws";
	const extension = options.extension ?? { id: options.name };
	const identity = options.identity ?? {
		id: createInstanceId(),
		extension
	};
	return {
		url,
		name: options.name,
		token: options.token,
		connector: options.connector ?? createDefaultProtocolConnector(url),
		handshake: options.handshake ?? "module",
		connectTimeoutMs: options.connectTimeoutMs ?? 15e3,
		possibleEvents: options.possibleEvents ?? [],
		extension,
		identity,
		dependencies: options.dependencies ?? [],
		configSchema: options.configSchema,
		heartbeat: normalizeHeartbeatOptions(options.heartbeat),
		autoConnect: options.autoConnect ?? true,
		autoReconnect: options.autoReconnect ?? true,
		maxReconnectAttempts: options.maxReconnectAttempts ?? -1,
		onError: options.onError ?? (() => {}),
		onClose: options.onClose ?? (() => {}),
		onReady: options.onReady ?? (() => {}),
		onStateChange: options.onStateChange ?? (() => {}),
		onAnyMessage: options.onAnyMessage ?? (() => {}),
		onAnySend: options.onAnySend ?? (() => {})
	};
}
function createConnectionTimeoutError(timeout) {
	return /* @__PURE__ */ new Error(`Connection timed out after ${timeout}ms`);
}
function createAbortError() {
	return /* @__PURE__ */ new Error("Connection aborted");
}
var Client = class {
	opts;
	transport;
	eventListeners = /* @__PURE__ */ new Map();
	stateListeners = /* @__PURE__ */ new Set();
	status = "idle";
	connectTask;
	failureReason;
	constructor(options) {
		this.opts = normalizeOptions(options);
		this.transport = createClient$1({
			connector: this.opts.connector,
			reconnect: this.createReconnectOptions(),
			heartbeat: this.createHeartbeatOptions(),
			prepare: (context) => this.prepareProtocolConnection(context)
		});
		this.transport.onMessage(({ message }) => {
			this.handleMessage(message);
		});
		this.transport.onStateChange(({ previousState, state }) => {
			this.handleTransportStateChange(previousState, state);
		});
		if (this.opts.autoConnect) this.connect().catch((error) => {
			const normalized = this.normalizeError(error, "Failed to connect websocket client");
			this.failureReason = normalized;
			this.opts.onError(normalized);
		});
	}
	get connectionStatus() {
		return this.status;
	}
	get isReady() {
		return this.status === "ready";
	}
	get isSocketOpen() {
		return this.transport.state === "open" || this.transport.state === "preparing" || this.transport.state === "ready";
	}
	get lastError() {
		return this.failureReason;
	}
	async connect(options) {
		if (this.status === "ready") return;
		if (!this.connectTask && this.transport.state === "reconnecting") return this.waitForConnection(this.waitForReady(), options);
		if (!this.connectTask && (this.transport.state === "open" || this.transport.state === "preparing")) return this.waitForConnection(this.waitForReady(), options);
		if (!this.connectTask) this.connectTask = this.transport.connect().finally(() => {
			this.connectTask = void 0;
		});
		return this.waitForConnection(this.connectTask, options);
	}
	ready(options) {
		return this.connect(options);
	}
	ensureConnected(options) {
		return this.connect(options);
	}
	onConnectionStateChange(callback) {
		this.stateListeners.add(callback);
		return () => {
			this.stateListeners.delete(callback);
		};
	}
	onEvent(event, callback) {
		let listeners = this.eventListeners.get(event);
		if (!listeners) {
			listeners = /* @__PURE__ */ new Set();
			this.eventListeners.set(event, listeners);
		}
		listeners.add(callback);
		return () => {
			this.offEvent(event, callback);
		};
	}
	offEvent(event, callback) {
		const listeners = this.eventListeners.get(event);
		if (!listeners) return;
		if (callback) {
			listeners.delete(callback);
			if (!listeners.size) this.eventListeners.delete(event);
			return;
		}
		this.eventListeners.delete(event);
	}
	send(data) {
		const payload = this.createPayload(data);
		if (!this.transport.send(payload).ok) return false;
		this.opts.onAnySend(payload);
		return true;
	}
	sendOrThrow(data) {
		if (!this.send(data)) throw new Error(`Client is not connected, current status: ${this.status}`);
	}
	close(code, reason) {
		this.transport.close(code, reason);
	}
	createReconnectOptions() {
		if (!this.opts.autoReconnect) return false;
		return {
			retries: (attempt, error) => {
				if (isTerminalAuthenticationServerErrorMessage(this.normalizeError(error, "Failed to connect websocket client").message)) return false;
				return this.opts.maxReconnectAttempts === -1 || attempt <= this.opts.maxReconnectAttempts;
			},
			onFailed: (error) => {
				const normalized = this.normalizeError(error, "Failed to connect websocket client");
				if (this.failureReason === normalized) return;
				this.failureReason = normalized;
				this.opts.onError(normalized);
			}
		};
	}
	createHeartbeatOptions() {
		if (!this.opts.heartbeat) return false;
		return {
			mode: "message",
			interval: this.opts.heartbeat.pingInterval,
			timeout: this.opts.heartbeat.readTimeout,
			message: () => this.createPayload({
				type: "transport:connection:heartbeat",
				data: {
					kind: MessageHeartbeatKind.Ping,
					message: this.opts.heartbeat ? this.opts.heartbeat.message : MessageHeartbeat.Ping,
					at: Date.now()
				}
			})
		};
	}
	async prepareProtocolConnection(context) {
		if (this.opts.handshake === "manual") {
			if (!context.reconnecting) return;
			this.transitionTo("authenticating");
			await this.waitForManualReconnectHandshake(context);
			return;
		}
		if (this.opts.token) {
			this.transitionTo("authenticating");
			context.send(this.createPayload({
				type: "module:authenticate",
				data: { token: this.opts.token }
			}));
			await context.waitFor((message) => {
				const result = this.consumePrepareMessage(message);
				if (result.error) throw result.error;
				return message.type === "module:authenticated" && message.data.authenticated === true;
			}, { timeout: this.opts.connectTimeoutMs });
		}
		this.transitionTo("announcing");
		context.send(this.createPayload({
			type: "extension:module:announce",
			data: {
				name: this.opts.name,
				identity: this.opts.identity,
				possibleEvents: this.opts.possibleEvents,
				configSchema: this.opts.configSchema,
				dependencies: this.opts.dependencies
			}
		}));
		await context.waitFor((message) => {
			const result = this.consumePrepareMessage(message);
			if (result.error) throw result.error;
			return result.ready;
		}, { timeout: this.opts.connectTimeoutMs });
	}
	async waitForManualReconnectHandshake(context) {
		await context.waitFor((message) => {
			const result = this.consumePrepareMessage(message);
			if (result.error) throw result.error;
			return message.type === "peer:authenticated" && message.data.authenticated === true;
		}, { timeout: this.opts.connectTimeoutMs });
		this.transitionTo("announcing");
		await context.waitFor((message) => {
			const result = this.consumePrepareMessage(message);
			if (result.error) throw result.error;
			return message.type === "extension:announced" && message.data.identity.id === this.opts.extension.id;
		}, { timeout: this.opts.connectTimeoutMs });
	}
	consumePrepareMessage(message) {
		const error = this.errorFromServerEvent(message);
		if (error) {
			this.failureReason = error;
			return {
				ready: false,
				error
			};
		}
		if (message.type === "extension:module:announced") return { ready: this.isSelfModuleAnnouncement(message) };
		if (message.type === "registry:modules:sync") return { ready: this.hasSelfModuleInRegistrySync(message) };
		return { ready: false };
	}
	async handleMessage(message) {
		this.opts.onAnyMessage(message);
		const error = this.errorFromServerEvent(message);
		if (error) {
			this.failureReason = error;
			this.opts.onError(error);
		}
		if (message.type === "transport:connection:heartbeat" && message.data.kind === MessageHeartbeatKind.Ping) this.send({
			type: "transport:connection:heartbeat",
			data: {
				kind: MessageHeartbeatKind.Pong,
				message: MessageHeartbeat.Pong,
				at: Date.now()
			}
		});
		const listeners = this.eventListeners.get(message.type);
		if (!listeners?.size) return;
		const results = await Promise.allSettled(Array.from(listeners).map((listener) => Promise.resolve(listener(message))));
		for (const result of results) if (result.status === "rejected") {
			this.failureReason = this.normalizeError(result.reason, "Client event listener failed");
			this.opts.onError(result.reason);
		}
	}
	handleTransportStateChange(previousState, state) {
		if (state === "ready") {
			this.transitionTo("ready");
			this.opts.onReady();
			return;
		}
		const nextStatus = this.mapTransportStatus(state);
		if (!nextStatus) return;
		this.transitionTo(nextStatus);
		if (previousState === "ready" && state === "reconnecting") this.opts.onClose();
		else if (state === "closed") this.opts.onClose();
	}
	mapTransportStatus(state) {
		switch (state) {
			case "idle":
			case "connecting":
			case "reconnecting":
			case "closing":
			case "closed":
			case "failed": return state;
			case "open":
			case "preparing":
			case "ready": return;
		}
	}
	transitionTo(status) {
		if (this.status === status) return;
		const previousStatus = this.status;
		this.status = status;
		const context = {
			previousStatus,
			status
		};
		this.opts.onStateChange(context);
		for (const listener of this.stateListeners) listener(context);
	}
	async waitForConnection(connectPromise, options) {
		if (!options?.timeout && !options?.abortSignal) return connectPromise;
		const timeout = options?.timeout;
		if (typeof timeout !== "undefined" && timeout <= 0) throw createConnectionTimeoutError(timeout);
		const abortSignal = options?.abortSignal;
		if (abortSignal?.aborted) throw createAbortError();
		let timeoutHandle;
		let removeAbortListener;
		try {
			await Promise.race([connectPromise, new Promise((_, reject) => {
				if (typeof timeout !== "undefined") timeoutHandle = setTimeout(() => {
					reject(createConnectionTimeoutError(timeout));
				}, timeout);
				if (abortSignal) {
					const onAbort = () => reject(createAbortError());
					abortSignal.addEventListener("abort", onAbort, { once: true });
					removeAbortListener = () => abortSignal.removeEventListener("abort", onAbort);
				}
			})]);
		} finally {
			if (timeoutHandle) clearTimeout(timeoutHandle);
			removeAbortListener?.();
		}
	}
	waitForReady() {
		if (this.status === "ready") return Promise.resolve();
		return new Promise((resolve, reject) => {
			const dispose = this.onConnectionStateChange(({ status }) => {
				if (status === "ready") {
					dispose();
					resolve();
					return;
				}
				if (status === "failed" || status === "closed") {
					dispose();
					reject(this.failureReason ?? /* @__PURE__ */ new Error(`Client connection ended with status: ${status}`));
				}
			});
		});
	}
	errorFromServerEvent(message) {
		if (message.type !== "error") return;
		const errorMessage = typeof message.data.message === "string" ? message.data.message : "Unknown server error";
		const parsed = parseServerErrorMessage(errorMessage);
		if (parsed.code === "unknown") return new Error(errorMessage);
		return new Error(parsed.message);
	}
	normalizeError(error, fallback) {
		return error instanceof Error ? error : new Error(errorMessageFrom(error) ?? fallback);
	}
	isSelfModuleAnnouncement(event) {
		return event.data.name === this.opts.name && event.data.identity?.id === this.opts.identity.id;
	}
	hasSelfModuleInRegistrySync(event) {
		return event.data.modules.some((module) => module.name === this.opts.name && module.identity?.id === this.opts.identity.id);
	}
	createPayload(data) {
		return {
			...data,
			metadata: {
				...data.metadata,
				source: data.metadata?.source ?? {
					kind: "plugin",
					...this.opts.identity,
					plugin: { id: this.opts.extension.id }
				},
				event: {
					...data.metadata?.event,
					id: data.metadata?.event?.id ?? createEventId()
				}
			}
		};
	}
};
function createClient(options) {
	return new Client(options);
}
//#endregion
//#region src/extension-peer.ts
/**
* Provides extension-level protocol helpers over a server-sdk protocol client.
*/
var WebSocketExtensionPeer = class {
	client;
	extension;
	constructor(options) {
		this.extension = options.extension;
		this.client = options.client ?? createClient({
			...options.clientOptions,
			name: options.extension.id,
			handshake: "manual",
			autoConnect: options.clientOptions?.autoConnect ?? false,
			autoReconnect: options.clientOptions?.autoReconnect ?? false
		});
	}
	connect(options) {
		return this.client.connect(options);
	}
	authenticatePeer(input = {}) {
		this.client.sendOrThrow({
			type: "peer:authenticate",
			data: input
		});
	}
	announceExtension(input = {}) {
		this.client.sendOrThrow({
			type: "extension:announce",
			data: {
				identity: this.extension,
				permissions: input.permissions
			}
		});
	}
	announceModule(input) {
		this.client.sendOrThrow({
			type: "extension:module:announce",
			data: {
				name: input.name,
				identity: {
					id: input.id,
					extension: this.extension,
					labels: input.labels
				},
				possibleEvents: input.possibleEvents ?? [],
				permissions: input.permissions,
				configSchema: input.configSchema,
				dependencies: input.dependencies
			}
		});
	}
	send(data) {
		return this.client.send(data);
	}
	onEvent(event, callback) {
		if (!this.client.onEvent) throw new Error("Wrapped extension peer client does not support event listeners.");
		return this.client.onEvent(event, callback);
	}
	close() {
		this.client.close();
	}
};
/** Creates a websocket extension peer over a server-sdk protocol client. */
function createWebSocketExtensionPeer(options) {
	return new WebSocketExtensionPeer(options);
}
//#endregion
export { Client, ContextUpdateStrategy, InvalidMessageError, WebSocketEventSource, WebSocketExtensionPeer, createClient, createTextProtocolConnector, createWebSocketExtensionPeer, parseEvent, stringifyEvent };

//# sourceMappingURL=index.mjs.map